import {
  faAdd,
  faEye,
  faUsers,
  faSave,
  faTrash,
  faPen,
  faChevronLeft,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AddNewWordModal from "components/modals/AddNewWordModal";
import AllWordsModal from "components/modals/AllWordsModal";
import ExerciseInformationModal from "components/modals/ExerciseInformationModal";
import React, { useEffect, useState } from "react";
import { Button, Card, Dropdown, Placeholder, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Toggle from "react-toggle";
import {
  getVocabularyById,
  updateVocabularyId,
  deleteStudySet,
} from "services/Teacher/Vocabulary.service";
import { GetUserRoles } from "utils/auth.utils";
import VocabularyListReports from "views/students/Vocabulary/VocabularyListReports";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const VocabularyDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const userRole = GetUserRoles();
  const [showModal, setShowModal] = useState(false);
  const [updatedStudySetDetail, setUpdatedStudySetDetail] = useState([]);
  const [targetLanguageTitle, setTargetLanguageTitle] = useState("");
  const [wordDetails, setWordDetails] = useState(null);
  const [sourceLanguageTitle, setSourceLanguageTitle] = useState("");
  const [showexerciseDetail, setShowexerciseDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibility, setVisibility] = useState(""); // State to manage visibility
  const [isDeleting, setIsDeleting] = useState(false); // State for delete loading
  const [studySetTitle, setStudySetTitle] = useState(""); // State for study set title
  const [isEditingTitle, setIsEditingTitle] = useState(false); // State for edit mode
  const [tempTitle, setTempTitle] = useState(""); // Temporary title for editing
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false); // Loading state for title update

  const handleCloseexerciseDetailModal = () => setShowexerciseDetail(false);
  const handleShowexerciseDetailModal = () => setShowexerciseDetail(true);

  const params = useParams();
  const id = params.id;

  const fetchStudySetDetails = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await getVocabularyById(id);
      setUpdatedStudySetDetail(response?.data?.result?.words);
      setTargetLanguageTitle(response?.data?.result?.targetLanguage?.title);
      setSourceLanguageTitle(response?.data?.result?.sourceLanguage?.title);
      setVisibility(response?.data?.result?.visibility); // Set visibility state from API response
      setStudySetTitle(response?.data?.result?.title || ""); // Set study set title
    } catch (error) {
      console.error("Error fetching Study Set details:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStudySetDetails();
  }, [fetchStudySetDetails]);

  const handleAddWord = async (newWordId) => {
    const updatedWords = [...updatedStudySetDetail, newWordId];
    try {
      await updateVocabularyId(id, { words: updatedWords });
      await fetchStudySetDetails();
    } catch (error) {
      console.error("Error adding word to Study Set:", error);
    }
  };

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setWordDetails(null);
  };

  const handleClose = () => setShow(false);

  // Handle visibility toggle change
  const handleVisibilityChange = async (e) => {
    const newVisibility = e.target.checked ? "public" : "private";
    setVisibility(newVisibility); // Update the visibility state

    try {
      // Update visibility in the API
      await updateVocabularyId(id, { visibility: newVisibility });
      toast.success(`${t("Study Set visibility changed to")} ${newVisibility}`);
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast.error(t("Failed to update visibility"));
    }
  };

  // Handle Save functionality
  const handleSave = () => {
    toast.success(t("Study Set saved successfully!"));
    navigate("/vocabulary");
  };

  // Handle Finish functionality (Delete Study Set)
  const handleFinish = async () => {
    if (
      window.confirm(
        t(
          "Are you sure you want to delete this study set? This action cannot be undone."
        )
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteStudySet(id);
        toast.success(t("Study Set deleted successfully!"));
        navigate("/vocabulary");
      } catch (error) {
        console.error("Error deleting study set:", error);
        toast.error(t("Failed to delete study set"));
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Handle title editing functions
  const handleStartEditTitle = () => {
    setTempTitle(studySetTitle);
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setTempTitle("");
    setIsEditingTitle(false);
  };

  const handleSaveTitle = async () => {
    if (!tempTitle.trim()) {
      toast.error(t("Title cannot be empty"));
      return;
    }

    setIsUpdatingTitle(true);
    try {
      await updateVocabularyId(id, { title: tempTitle.trim() });
      setStudySetTitle(tempTitle.trim());
      setIsEditingTitle(false);
      setTempTitle("");
      toast.success(t("Title updated successfully!"));
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error(t("Failed to update title"));
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between pb-4 gap-3">
        <div
          className="d-flex flex-column gap-1 w-100 w-lg-auto"
          style={{ minWidth: 0 }}
        >
          <button
            type="button"
            className="vocab-breadcrumb"
            onClick={() => navigate("/vocabulary")}
          >
            <FontAwesomeIcon icon={faChevronLeft} size="sm" />
            {t("Vocabulary")}
          </button>

          {isEditingTitle ? (
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                className="vocab-title-input"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveTitle();
                  } else if (e.key === "Escape") {
                    handleCancelEditTitle();
                  }
                }}
                autoFocus
                disabled={isUpdatingTitle}
              />
              <button
                type="button"
                className="vocab-icon-btn vocab-icon-btn--confirm"
                onClick={handleSaveTitle}
                disabled={isUpdatingTitle}
                aria-label={t("Save title")}
                title={t("Save title")}
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <button
                type="button"
                className="vocab-icon-btn vocab-icon-btn--cancel"
                onClick={handleCancelEditTitle}
                disabled={isUpdatingTitle}
                aria-label={t("Cancel")}
                title={t("Cancel")}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ minWidth: 0 }}
            >
              <h1
                className="vocab-title"
                title={studySetTitle || t("Untitled Study Set")}
              >
                {studySetTitle || t("Untitled Study Set")}
              </h1>
              <button
                type="button"
                className="vocab-edit-btn"
                onClick={handleStartEditTitle}
                aria-label={t("Edit title")}
                title={t("Edit title")}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
            </div>
          )}
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 w-100 w-lg-auto justify-content-start justify-content-lg-end">
          {/* Save and Finish Buttons */}
          <Button
            className="px-3 px-sm-4 py-2 border-0 button-style"
            style={{
              background: "linear-gradient(270deg, #28a745 0%, #20c997 100%)",
              fontSize: "clamp(0.875rem, 2vw, 1rem)",
            }}
            onClick={handleSave}
          >
            <FontAwesomeIcon icon={faSave} className="me-1 me-sm-2" />
            <span className="d-none d-sm-inline">{t("Save")}</span>
          </Button>

          <Button
            className="px-3 px-sm-4 py-2 border-0 button-style"
            style={{
              background: "linear-gradient(270deg, #dc3545 0%, #c82333 100%)",
              fontSize: "clamp(0.875rem, 2vw, 1rem)",
            }}
            onClick={handleFinish}
            disabled={isDeleting}
          >
            <FontAwesomeIcon icon={faTrash} className="me-1 me-sm-2" />
            <span className="d-none d-sm-inline">
              {isDeleting ? t("Deleting...") : t("Delete")}
            </span>
          </Button>

          {userRole === "superUser" ? (
            <div
              className="d-flex align-items-center gap-2 px-2 py-1 rounded-3"
              style={{ 
                background: "#cecece",
                fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
              }}
            >
              <span className="fw-bold d-none d-md-inline">{t("Library visibility")}</span>
              <span className="fw-bold d-md-none">{t("Visibility")}</span>
              <label>
                <Toggle
                  className="mt-2"
                  icons={{
                    checked: (
                      <span style={{ fontSize: "8px" }}>{t("ON")}</span>
                    ),
                    unchecked: (
                      <span style={{ fontSize: "8px" }}>{t("OFF")}</span>
                    ),
                  }}
                  checked={visibility === "public"}
                  onChange={handleVisibilityChange}
                />
              </label>
            </div>
          ) : (
            <Button
              className="px-3 px-sm-4 py-2 border-0 button-style"
              style={{
                background: "linear-gradient(270deg, #272727 0%, #4B4B4B 100%)",
                fontSize: "clamp(0.875rem, 2vw, 1rem)",
              }}
              onClick={handleShowexerciseDetailModal}
            >
              <FontAwesomeIcon icon={faUsers} className="me-1 me-sm-2" />
              <span className="d-none d-sm-inline">{t("Assign")}</span>
            </Button>
          )}

          <Dropdown>
            <Dropdown.Toggle
              id="dropdown-custom-components"
              variant="secondary"
              className="py-2 px-3 px-sm-4 border-0 button-style"
              style={{
                fontSize: "clamp(0.875rem, 2vw, 1rem)",
              }}
            >
              <FontAwesomeIcon className="pe-1 pe-sm-2" icon={faAdd} />
              <span className="d-none d-sm-inline">{t("Add words")}</span>
              <span className="d-sm-none">{t("Add")}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={handleShowModal}>
                {t("Add New Word")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading ? (
        <Card className="mt-5 border-0">
          <Placeholder animation="glow">
            <Placeholder xs={12} height={200} />
          </Placeholder>
          <Placeholder animation="glow">
            <Placeholder xs={6} height={200} />
          </Placeholder>
          <Placeholder animation="glow">
            <Placeholder xs={9} height={200} />
          </Placeholder>
          <Placeholder animation="glow">
            <Placeholder xs={12} height={200} />
          </Placeholder>
        </Card>
      ) : (
        <Card className="border-0">
          <VocabularyListReports
            studySetDetail={updatedStudySetDetail}
            targetLanguageTitle={targetLanguageTitle}
            sourceLanguageTitle={sourceLanguageTitle}
            studySetId={id}
            setWordDetails={setWordDetails}
            handleShowModal={handleShowModal}
            reload={fetchStudySetDetails}
            libraryStatus={visibility === "public" ? true : false}
          />
        </Card>
      )}

      {/* Modal for adding new word */}
      <AddNewWordModal
        show={showModal}
        handleClose={handleCloseModal}
        studySetId={id}
        wordData={wordDetails}
        addWordToVocabulary={handleAddWord}
        reload={fetchStudySetDetails}
      />

      {/* Add from Library Modal */}
      <AllWordsModal
        show={show}
        handleClose={handleClose}
        addWordToVocabulary={handleAddWord}
      />

      <ExerciseInformationModal
        show={showexerciseDetail}
        onHide={handleCloseexerciseDetailModal}
        type="studySet"
      />
    </>
  );
};

export default VocabularyDetail;